<script setup lang="ts">
import { storeToRefs } from 'pinia';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { type RouteLocationRaw, useRoute } from 'vue-router';

import { useFeatureToggleQuery } from '@/composables/useFeatureToggle';
import {
  buildCrossDirectoryLocationRoute,
  mergeAgencyDirectoryStashQuery,
} from '@/helpers/Directory/directoryToggleRouteQuery';
import { getAgentHomeRouteName, getHomeRouteName } from '@/helpers/Url/Url';
import { useLocale } from '@/router/routeMeta';
import { useAgencyProfilesStore } from '@/stores/agencyProfiles';

const props = withDefaults(defineProps<{ activeType?: 'agency' | 'agent' }>(), {
  activeType: 'agency',
});
const { t } = useI18n();
const route = useRoute();
const locale = useLocale();
const featureToggleQuery = useFeatureToggleQuery();
const { lastAgencyDirectoryQuery } = storeToRefs(useAgencyProfilesStore());

const isAgentActive = computed(() => props.activeType === 'agent');

const crossDirectoryLocationRoute = computed(() =>
  buildCrossDirectoryLocationRoute(route, featureToggleQuery.value),
);

const agencyDirectoryRoute = computed<RouteLocationRaw>(() => {
  const { params, query } = crossDirectoryLocationRoute.value;

  return {
    name: getHomeRouteName(locale.value),
    params,
    query: isAgentActive.value
      ? mergeAgencyDirectoryStashQuery(query, lastAgencyDirectoryQuery.value)
      : query,
  };
});

const agentDirectoryRoute = computed<RouteLocationRaw>(() => ({
  name: getAgentHomeRouteName(locale.value),
  ...crossDirectoryLocationRoute.value,
}));
</script>

<template>
  <div :class="$style.searchType">
    <span :class="$style.headline">{{ t('searchTypeToggle.label') }}</span>
    <nav :class="$style.options" :aria-label="t('searchTypeToggle.label')">
      <RouterLink
        :to="agentDirectoryRoute"
        :class="[$style.option, isAgentActive && $style.selectedOption]"
        :aria-current="isAgentActive ? 'page' : undefined"
        data-dd-action-name="SearchBar Agent Toggle"
        data-test="search-type-agent"
      >
        {{ t('searchTypeToggle.agent') }}
      </RouterLink>
      <RouterLink
        :to="agencyDirectoryRoute"
        :class="[$style.option, !isAgentActive && $style.selectedOption]"
        :aria-current="!isAgentActive ? 'page' : undefined"
        data-dd-action-name="SearchBar Agency Toggle"
        data-test="search-type-agency"
      >
        {{ t('searchTypeToggle.agency') }}
      </RouterLink>
    </nav>
  </div>
</template>

<style lang="scss" module>
@use '@/assets/variables' as *;

.searchType {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.headline {
  @include label;
}

.options {
  box-sizing: border-box;
  display: inline-flex;
  align-items: stretch;
  min-height: 44px;
  margin-top: $space-75;
  overflow: hidden;
  background-color: $color-white;
  border: 1px solid $color-grey-50;
  border-radius: $border-radius-l;
}

.option {
  box-sizing: border-box;
  display: flex;

  /* Both options share the same width, whichever label is longer. */
  flex: 1 1 0;
  align-items: center;
  justify-content: center;
  min-width: $space-800;
  padding: $space-50 $space-150;
  color: $color-grey-100;
  white-space: nowrap;

  @include body-2-semi-bold;

  &:hover {
    text-decoration: none;
    background-color: $color-grey-30;
  }
}

.selectedOption {
  color: var(--theme-color-text-fill);
  background-color: var(--theme-color-primary);

  &:hover {
    background-color: var(--theme-color-primary-hover);
  }
}
</style>
